package br.gov.go.pge.permanenciaweb.site

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.BoxSizing
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.boxSizing
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.defer.DeferringHost
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.font
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.vh

/**
 * Page-level rules that can't be scoped to a single widget, ported from the old static UI.
 * Everything else now lives in per-widget `CssStyle`s.
 */
@InitSilk
fun initGlobalStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyleBase("*") {
        Modifier.boxSizing(BoxSizing.BorderBox)
    }

    // `html` stays at height:100% (definite), so the body's `min-height:100%` resolves against the
    // viewport while still growing with content. Pinning the body itself to `height:100%` made every
    // background stop at the first viewport once a page grew past it (admin roster with many rows).
    ctx.stylesheet.registerStyleBase("html") {
        Modifier
            .height(100.percent)
            .backgroundColor(IndustrialPalette.Surface)
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .minHeight(100.percent)
            .margin(0.cssRem)
            .backgroundColor(IndustrialPalette.Surface)
            .color(IndustrialPalette.Ink)
            .fontFamily("Segoe UI", "system-ui", "sans-serif")
            // rem (not px) so the page follows the user's font scaling.
            .fontSize(0.875.cssRem)
    }

    ctx.stylesheet.registerStyleBase("button, select, input") {
        // Escape hatch: `font: inherit` is a shorthand with no Modifier equivalent; native controls
        // (login inputs, filter selects) must stay on the page font like the old stylesheet did.
        Modifier.styleModifier { font("inherit") }
    }

    // Indeterminate thin ruler shown at the bottom of the photo dialog while either image loads.
    ctx.stylesheet.registerKeyframes("photo-loading-ruler") {
        from { Modifier.styleModifier { property("transform", "translateX(-100%)") } }
        to { Modifier.styleModifier { property("transform", "translateX(300%)") } }
    }
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    val scope = rememberCoroutineScope()
    val appState = remember { AppState(scope) }

    SilkApp {
        CompositionLocalProvider(LocalAppState provides appState) {
            Surface(
                Modifier
                    .fillMaxWidth()
                    // The Surface paints the app background over at least the viewport and grows with
                    // the content. The `html`/`body` page rules below don't reach every browser: Silk
                    // scopes them under `@scope (.silk-light, .silk-dark)` and Firefox-based browsers
                    // (e.g. Zen) don't match the scoping root, so they'd collapse to content height
                    // and let the browser's dark canvas show around the app.
                    .minHeight(100.vh)
                    // `flow-root`: contain the first child's margins (the login card's 15vh) so the
                    // Surface starts at y=0 instead of being pushed down by margin collapse.
                    .styleModifier { property("display", "flow-root") },
            ) {
                // Deferred Silk overlays (removal dialog, filter popovers) render at the end of this scope,
                // inheriting the Surface palette; without a host, `Deferred` throws at render time.
                DeferringHost {
                    content()
                }
            }
        }
    }
}
